import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { FinanceService } from '../../../api/finance.service';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SelectedPeriod {
  label: string;
  dateFrom: string;
  dateTo: string;
  periodType: 'year' | 'quarter' | 'month';
}

interface Props {
  onSelectPeriod: (period: SelectedPeriod) => void;
  refreshKey?: number;
}

// ── Quarter / month definitions ───────────────────────────────────────────────

const QUARTERS = [
  {
    q: 1,
    label: 'Q1',
    range: 'Jan – Mar',
    months: ['January', 'February', 'March'],
    startMonth: 0,
  },
  {
    q: 2,
    label: 'Q2',
    range: 'Apr – Jun',
    months: ['April', 'May', 'June'],
    startMonth: 3,
  },
  {
    q: 3,
    label: 'Q3',
    range: 'Jul – Sep',
    months: ['July', 'August', 'September'],
    startMonth: 6,
  },
  {
    q: 4,
    label: 'Q4',
    range: 'Oct – Dec',
    months: ['October', 'November', 'December'],
    startMonth: 9,
  },
];

// ── Date helpers ──────────────────────────────────────────────────────────────

function pad(n: number) {
  return String(n).padStart(2, '0');
}

// Built from parts, not toISOString(): a local-midnight Date converted to UTC
// lands on the previous day east of UTC (and in the UK during BST), which made
// Q4 end on 30 December.
function lastDay(year: number, month: number): string {
  const day = new Date(year, month + 1, 0).getDate();
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

function yearPeriod(year: number): SelectedPeriod {
  return {
    label: String(year),
    dateFrom: `${year}-01-01`,
    dateTo: `${year}-12-31`,
    periodType: 'year',
  };
}

function quarterPeriod(year: number, qIndex: number): SelectedPeriod {
  const q = QUARTERS[qIndex];
  return {
    label: `${q.label} ${year} (${q.range})`,
    dateFrom: `${year}-${pad(q.startMonth + 1)}-01`,
    dateTo: lastDay(year, q.startMonth + 2),
    periodType: 'quarter',
  };
}

function monthPeriod(
  year: number,
  monthIndex: number,
  monthName: string,
): SelectedPeriod {
  return {
    label: `${monthName} ${year}`,
    dateFrom: `${year}-${pad(monthIndex + 1)}-01`,
    dateTo: lastDay(year, monthIndex),
    periodType: 'month',
  };
}

// ── View Dashboard button ─────────────────────────────────────────────────────

function ViewBtn({
  period,
  onSelect,
}: {
  period: SelectedPeriod;
  onSelect: (p: SelectedPeriod) => void;
}) {
  return (
    <Button
      size="small"
      variant="contained"
      endIcon={<Iconify icon="eva:arrow-forward-fill" width={14} />}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(period);
      }}
      sx={{ ml: 'auto', mr: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      View Dashboard
    </Button>
  );
}

// ── Month row (inside a quarter accordion) ────────────────────────────────────

function MonthRow({
  label,
  period,
  onSelect,
  isLast,
}: {
  label: string;
  period: SelectedPeriod;
  onSelect: (p: SelectedPeriod) => void;
  isLast: boolean;
}) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          py: 1.25,
          px: 2,
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          cursor: 'default',
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'text.disabled',
            mr: 1.5,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ flex: 1 }}>
          {label}
        </Typography>
        <ViewBtn period={period} onSelect={onSelect} />
      </Stack>
      {!isLast && <Divider sx={{ mx: 2 }} />}
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function FinancePeriodSelector({
  onSelectPeriod,
  refreshKey,
}: Props) {
  const [years, setYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedYear, setExpandedYear] = useState<number | false>(false);
  const [expandedQuarter, setExpandedQuarter] = useState<string | false>(false);

  useEffect(() => {
    setLoading(true);
    new FinanceService()
      .getAvailableYears()
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setYears(data);
        if (data.length > 0) setExpandedYear((prev) => prev || data[0]);
      })
      .catch(() => {
        const currentYear = new Date().getFullYear();
        setYears([currentYear]);
        setExpandedYear(currentYear);
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (years.length === 0) {
    return (
      <Card sx={{ py: 8, textAlign: 'center' }}>
        <Iconify
          icon="eva:inbox-fill"
          width={48}
          sx={{ color: 'text.disabled', display: 'block', mx: 'auto', mb: 2 }}
        />
        <Typography variant="subtitle1" color="text.secondary">
          No transaction data found
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
          Import a bank statement first, then come back to view period
          dashboards.
        </Typography>
      </Card>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle1" fontWeight={600}>
            Select a period
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Choose a year, quarter, or month to open its Finance Dashboard
          </Typography>
        </Stack>
      </Stack>

      {/* ── Year accordions ─────────────────────────────────────────────── */}
      {years.map((year) => (
        <Accordion
          key={year}
          expanded={expandedYear === year}
          onChange={() => setExpandedYear(expandedYear === year ? false : year)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { borderColor: 'primary.light' },
          }}
        >
          <AccordionSummary
            expandIcon={
              <Iconify
                icon="eva:chevron-down-fill"
                width={20}
                sx={{ color: 'text.secondary' }}
              />
            }
            sx={{
              px: 2.5,
              minHeight: 56,
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 1,
                my: 1,
              },
            }}
          >
            {/* Year icon */}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Iconify
                icon="eva:calendar-fill"
                width={16}
                sx={{ color: 'primary.main' }}
              />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              {year}
            </Typography>
            <ViewBtn period={yearPeriod(year)} onSelect={onSelectPeriod} />
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0, pb: 1 }}>
            <Divider />
            <Stack sx={{ pt: 1, px: 1.5 }} spacing={0.5}>
              {/* ── Quarter accordions ─────────────────────────────────── */}
              {QUARTERS.map((q, qi) => {
                const qKey = `${year}-Q${q.q}`;
                const qExpanded = expandedQuarter === qKey;

                return (
                  <Accordion
                    key={q.q}
                    expanded={qExpanded}
                    onChange={() =>
                      setExpandedQuarter(qExpanded ? false : qKey)
                    }
                    disableGutters
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '6px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { borderColor: 'secondary.light' },
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Iconify
                          icon="eva:chevron-down-fill"
                          width={16}
                          sx={{ color: 'text.secondary' }}
                        />
                      }
                      sx={{
                        px: 2,
                        minHeight: 44,
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 1,
                          my: 0.5,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 0.75,
                          bgcolor: 'secondary.lighter',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          fontWeight={700}
                          color="secondary.dark"
                          sx={{ fontSize: 10 }}
                        >
                          {q.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ flex: 1 }}
                      >
                        {q.label}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {q.range}
                        </Typography>
                      </Typography>
                      <ViewBtn
                        period={quarterPeriod(year, qi)}
                        onSelect={onSelectPeriod}
                      />
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0, pb: 0.5 }}>
                      <Divider />
                      <Box sx={{ pt: 0.5, px: 1 }}>
                        {/* ── Month rows ───────────────────────────────── */}
                        {q.months.map((monthName, mi) => (
                          <MonthRow
                            key={monthName}
                            label={monthName}
                            period={monthPeriod(
                              year,
                              q.startMonth + mi,
                              monthName,
                            )}
                            onSelect={onSelectPeriod}
                            isLast={mi === q.months.length - 1}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}
